import { AppId } from '../constants';
import type { OptionalAppRoute } from '../types';
import { doesLegacyViewMatch, type LegacyViewContext } from './doesLegacyViewMatch';
import { getMatchedUrlLength } from './getMatchedUrlLength';

/** How a navigation link matches the page in view. */
export interface NavLinkMatch {
    /** Length of the URL the link matched. -1 for a legacy match: the URL says nothing there. */
    matchedUrlLength: number;
    /** Whether the link matched the legacy Angular JS view, through the router context. */
    isLegacyMatch: boolean;
}

/** What a navigation link is matched against. */
export interface MatchNavLinkOptions {
    /** Pathname of the page in view. */
    pathname: string;
    /** Router context. It reports the legacy Angular JS view, when there is one. */
    context?: LegacyViewContext;
}

/**
 * Whether, and how, a navigation link points at the page in view. Returns null when it does not.
 *
 * An external link leaves the application, so it never matches. While the router context reports
 * a legacy view, the URL says nothing about the page in view, so only a legacy match counts.
 * Otherwise the link matches the pathname as a prefix, and the result carries the matched length.
 */
export const matchNavLink = (
    route: OptionalAppRoute<any> | undefined,
    { pathname, context }: MatchNavLinkOptions,
): NavLinkMatch | null => {
    if (!route || route.appId === AppId.external) {
        return null;
    }

    if (context?.currentView) {
        return doesLegacyViewMatch(context, route) ? { matchedUrlLength: -1, isLegacyMatch: true } : null;
    }

    const matchedUrlLength = getMatchedUrlLength(route, pathname);

    return matchedUrlLength < 0 ? null : { matchedUrlLength, isLegacyMatch: false };
};
